/**
 * PATH: apps/quote/src/components/builder/sheets/terms-sheet/TemplateSelector.tsx
 * PURPOSE: SAM-style dropdown for selecting and applying terms
 *          templates. Each template row exposes two affordances:
 *          "Replace" (overwrites the editor) and "Add missing" (only
 *          appends template lines that aren't already in the editor).
 * WHY: SAM's quote-notes editor has both Replace and Add-missing
 *      affordances per template. Quotech only had Replace.
 */

import { useState } from 'react'
import {
  FileText,
  Plus,
  ChevronDown,
  Loader2,
  Replace,
  PlusCircle,
} from 'lucide-react'
import { cn } from '@/lib/utils'

export interface TermsTemplate {
  id: string
  name: string
  description: string
  content: string
  is_default: boolean
}

export type TemplateApplyMode = 'replace' | 'add-missing'

export interface TemplateSelectorProps {
  templates: TermsTemplate[]
  /**
   * SAM-style apply path: caller decides whether to wipe (`replace`)
   * the editor or append only the missing lines (`add-missing`).
   */
  onSelect: (template: TermsTemplate, mode: TemplateApplyMode) => void
  isLoading: boolean
}

export function TemplateSelector({
  templates,
  onSelect,
  isLoading,
}: TemplateSelectorProps) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
          'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700',
          'hover:bg-slate-50 dark:hover:bg-slate-700',
        )}
        disabled={isLoading}
      >
        {isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <FileText className="w-4 h-4" />
        )}
        <span>Apply Template</span>
        <ChevronDown className="w-4 h-4" />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute left-0 top-full mt-1 z-20 w-96 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden">
            <div className="p-2 border-b border-slate-200 dark:border-slate-700">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Pick a template — Replace overwrites, Add missing only
                appends the lines that aren&apos;t already in your notes.
              </p>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {templates.length === 0 ? (
                <div className="p-4 text-center text-slate-500 dark:text-slate-400 text-sm">
                  No templates available
                </div>
              ) : (
                templates.map((template) => (
                  <div
                    key={template.id}
                    className="px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-700/50 flex items-center gap-2"
                  >
                    <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm truncate">
                          {template.name}
                        </span>
                        {template.is_default && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400">
                            Default
                          </span>
                        )}
                      </div>
                      {template.description && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                          {template.description}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        title="Replace current notes with this template"
                        onClick={() => {
                          onSelect(template, 'replace')
                          setIsOpen(false)
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600"
                      >
                        <Replace className="w-3 h-3" />
                        Replace
                      </button>
                      <button
                        type="button"
                        title="Append only the lines from this template that aren't already in your notes"
                        onClick={() => {
                          onSelect(template, 'add-missing')
                          setIsOpen(false)
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium text-teal-700 dark:text-teal-300 bg-teal-50 dark:bg-teal-900/30 hover:bg-teal-100 dark:hover:bg-teal-900/50"
                      >
                        <PlusCircle className="w-3 h-3" />
                        Add missing
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="p-2 border-t border-slate-200 dark:border-slate-700">
              <button
                onClick={() => setIsOpen(false)}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded"
              >
                <Plus className="w-4 h-4" />
                Create New Template
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
